import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const TableRenderer = ({ data, config = {} }) => {
  const tableRef = useRef(null);

  useEffect(() => {
    if (!data || !tableRef.current) return;

    // Clear previous content
    d3.select(tableRef.current).selectAll("*").remove();

    const {
      title = '',
      headers = [],
      rows = [],
      styling = {},
      width = 800,
      height = 'auto'
    } = data;

    const container = d3.select(tableRef.current);

    // Add title if provided
    if (title) {
      container
        .append('div')
        .style('font-size', '18px')
        .style('font-weight', 'bold')
        .style('margin-bottom', '16px')
        .style('color', '#1565c0')
        .text(title);
    }

    // Create table
    const table = container
      .append('table')
      .style('width', '100%')
      .style('border-collapse', 'collapse')
      .style('font-family', 'Arial, sans-serif')
      .style('box-shadow', '0 2px 8px rgba(0,0,0,0.1)')
      .style('border-radius', '8px')
      .style('overflow', 'hidden');

    // Create header
    if (headers.length > 0) {
      const thead = table.append('thead');
      const headerRow = thead.append('tr');

      headerRow
        .selectAll('th')
        .data(headers)
        .enter()
        .append('th')
        .style('background', styling.headerBackground || '#1565c0')
        .style('color', styling.headerColor || 'white')
        .style('padding', '12px 16px')
        .style('text-align', 'left')
        .style('font-weight', 'bold')
        .style('font-size', '14px')
        .style('border-bottom', '2px solid #0d47a1')
        .text(d => d);
    }

    // Create body
    const tbody = table.append('tbody');

    // Add rows
    const tableRows = tbody
      .selectAll('tr')
      .data(rows)
      .enter()
      .append('tr')
      .style('border-bottom', '1px solid #e0e0e0')
      .style('transition', 'background-color 0.2s ease')
      .on('mouseover', function() {
        d3.select(this).style('background-color', '#f5f5f5');
      })
      .on('mouseout', function() {
        d3.select(this).style('background-color', 'white');
      });

    // Add cells
    tableRows
      .selectAll('td')
      .data(d => d)
      .enter()
      .append('td')
      .style('padding', '12px 16px')
      .style('font-size', '13px')
      .style('vertical-align', 'middle')
      .style('color', (d, i, nodes) => {
        // Apply conditional styling based on content
        const text = d.toString().toLowerCase();
        if (text.includes('critical') || text.includes('🔴')) return '#d32f2f';
        if (text.includes('high') || text.includes('🟠')) return '#f57c00';
        if (text.includes('medium') || text.includes('🟡')) return '#fbc02d';
        if (text.includes('low') || text.includes('🟢')) return '#388e3c';
        return '#333';
      })
      .style('font-weight', (d, i, nodes) => {
        const text = d.toString().toLowerCase();
        if (text.includes('critical') || text.includes('urgent') || text.includes('immediate')) {
          return 'bold';
        }
        return 'normal';
      })
      .html(d => {
        // Handle special formatting
        let content = d.toString();
        
        // Make percentages bold
        content = content.replace(/(\d+\.?\d*%)/g, '<strong>$1</strong>');
        
        // Style status indicators
        if (content.includes('✅')) content = `<span style="color: #4caf50;">${content}</span>`;
        if (content.includes('❌')) content = `<span style="color: #f44336;">${content}</span>`;
        if (content.includes('⚠️')) content = `<span style="color: #ff9800;">${content}</span>`;
        
        return content;
      });

    // Add alternating row colors
    tableRows
      .style('background-color', (d, i) => i % 2 === 0 ? 'white' : '#fafafa');

  }, [data]);

  return (
    <div 
      ref={tableRef} 
      style={{ 
        width: '100%', 
        margin: '16px 0',
        overflow: 'auto'
      }} 
    />
  );
};

export default TableRenderer;